import { useLocation } from "react-router-dom"
import { PageData } from "types"
import {
  Box,
  Stack,
  forwardRef,
  useStyleConfig,
  useTheme,
  SystemStyleObject
} from "@chakra-ui/react"
import { RouterNavLink, Span } from "components/common"
import {
  ContentWrapper,
  ContentWrapperProps,
} from "components/LayoutComponents"
import { useMaxWidth } from "hooks"
import { SelectMenuNav } from "./SelectMenuNav"

export interface NavProps extends ContentWrapperProps {
  layoutLinkStyles?: SystemStyleObject
  navLinkSpacing?: number | { [key: string]: number }
  pages: PageData[]
}
export const Nav = forwardRef<NavProps, "nav">(
  ({ layoutLinkStyles, navLinkSpacing = 0, pages, ...rest }, ref) => {
    const styles = useStyleConfig("Nav")
    const navItemStyles = useStyleConfig("NavItem")

    const theme = useTheme()
    const { isMaxTabletView } = useMaxWidth()

    const linkStyleProps = {
      alignItems: "center" as const,
      borderLeft: "4px solid transparent",
      display: "flex" as const,
      paddingX: 6,
      paddingY: 4,
      whiteSpace: "nowrap" as const,
      color: "navLinkColor"
    }

    const hoverStyleProps = {
      backgroundColor: "navLinkHoverBackgroundColor",
      color: "navLinkHoverColor",
      textDecoration: "none"
    }

    const activeStyleProps = {
      borderLeft: "4px solid",
      borderLeftColor: theme.colors["navLinkActiveAccentColor"],
      boxShadow: "none",
      color: theme.colors["navLinkActiveColor"]
    }

    // Now let's incorporate layoutLinkStyles and navItemStyles properly
    // Apply them to the specific properties rather than spreading them blindly
    if (layoutLinkStyles) {
      // Apply any custom link styles from layout to the appropriate objects
      Object.assign(linkStyleProps, layoutLinkStyles)
    }

    if (navItemStyles) {
      // Apply navItemStyles selectively where they make sense
      // This way we have more control over which styles go where
      Object.assign(linkStyleProps, navItemStyles)
    }

    return (
      <Box
        as="nav"
        gridArea="nav"
        ref={ref}
        __css={styles}
        className="app-nav"
        {...rest}
      >
        <ContentWrapper paddingX={isMaxTabletView ? 0 : undefined}>
          {isMaxTabletView ? (
            <MobileNav pages={pages} />
          ) : (
            <Stack spacing={navLinkSpacing}>
              {pages.map((page, index) => (
                <RouterNavLink
                  key={page.id}
                  to={`/${page.slug}`}
                  {...linkStyleProps}
                  _hover={hoverStyleProps}
                  activeStyle={activeStyleProps}
                >
                  <Span className="nav-numbers">{index + 1}</Span>
                  <Span paddingRight={2}>{page.title}</Span>
                </RouterNavLink>
              ))}
            </Stack>
          )}
        </ContentWrapper>
      </Box>
    )
  }
)

const MobileNav = ({ pages }: { pages: PageData[] }) => {
  const location = useLocation()
  const currentPage = location.pathname.slice(1)

  return (
    <SelectMenuNav id="mobile-nav" size="sm">
      {pages.map(({ slug, id, title, mobileNavTitle }) => (
        <option key={id} value={slug} disabled={slug === currentPage}>
          {mobileNavTitle || title}
        </option>
      ))}
    </SelectMenuNav>
  )
}
